import { Button, Icon, Unifree } from '@yes.technology/react-toolkit'
import { FocusEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import useModal from '../../../shared/hooks/useModal'
import WebcamCapture from '../../../shared/WebcamCapture'
import useUploadS3, {
  UploadFileResult
} from '../../hooks/useUploadS3/useUploadS3'
import useDocument from '../../../shared/hooks/useDocument'
import FileUploader from '../../../shared/FileUploader'
import WebcamView from '../../../shared/WebcamView'
import { formatFileSize } from 'modules/shared/utils/formatFileSize'
import { useAppDispatch } from 'state/store'
import { fileToBase64 } from 'utils/file/file'
import { useDocumentURL } from 'modules/document/hooks'

type ActivityDocumentPhotoProps = {
  inputValue: string
  required?: boolean
  onChange: (value: string) => void
  label: string
  maxSizeInBytes?: number
}

type Values = {
  uuid?: string
  uuid_status?: string
  contentType?: string
  size?: string
  url?: string
  des?: string
  file?: string
}

const ActivityDocumentPhoto = ({
  inputValue,
  onChange,
  label,
  required,
  maxSizeInBytes
}: ActivityDocumentPhotoProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { createOrUpdateDocument } = useDocument()
  const { isOpenModal, openModal, closeModal } = useModal()
  const { uploadFile } = useUploadS3()

  const [view, setView] = useState(false)
  const [values, setValues] = useState<Values>()

  useEffect(() => {
    if (inputValue !== '') {
      setValues(JSON.parse(inputValue))
    }
  }, [inputValue])

  const onSuccessUploadFile = async ({ url, content }: UploadFileResult) => {
    const values = inputValue && JSON.parse(inputValue)
    const { document, statusDes } = await createOrUpdateDocument({
      uuid: values?.uuid,
      content_type: content.type,
      url,
      complement: JSON.stringify({ size: content.size })
    })

    onChange(
      JSON.stringify({
        ...values,
        uuid: document?.uuid,
        uuid_status: statusDes,
        des: document?.des,
        contentType: content.type,
        size: content.size,
        url,
        file: await fileToBase64(content)
      })
    )

    dispatch({
      type: 'api/RECEIVED',
      data: ''
    })
  }

  const capturePhoto = (img: string) => {
    dispatch({
      type: 'api/REQUESTED',
      data: ''
    })

    // close camera
    closeModal()

    uploadFile({
      file: img,
      fileType: 'base64'
    })
      .then(onSuccessUploadFile)
      .catch(onErrorUploadFile)
  }

  const onChangeDescription = (value: string) => {
    onChange(
      JSON.stringify({
        ...values,
        des: value
      })
    )
  }

  const saveDescription = (event: FocusEvent<HTMLInputElement>) => {
    if (!inputValue || !values?.file) {
      return
    }

    // workaround to solve a react issue when an event triggers a re-render the other events don't fire. As discussed this workaround should be deleted in a future refactor
    setTimeout(async () => {
      const values = JSON.parse(inputValue)
      const { document, statusDes } = await createOrUpdateDocument({
        uuid: values.uuid,
        des: event.target.value
      })

      onChange(
        JSON.stringify({
          ...values,
          uuid: document?.uuid,
          des: event.target.value,
          uuid_status: statusDes
        })
      )
    }, 500)
  }

  const onErrorUploadFile = () => {
    dispatch({
      type: 'api/RECEIVED',
      data: ''
    })
  }

  const uploadLocalFile = (file: File) => {
    dispatch({
      type: 'api/REQUESTED',
      data: ''
    })

    uploadFile({
      file
    })
      .then(onSuccessUploadFile)
      .catch(onErrorUploadFile)
  }

  const clearData = () => {
    onChange('')
    setValues({})
  }

  const closeWebcam = () => {
    // close camera
    closeModal()
  }

  const { documentURL } = useDocumentURL({ s3PrivateURL: values?.url })

  return (
    <>
      {!isOpenModal && (
        <div style={{ display: 'grid', rowGap: '8px' }}>
          <Unifree
            label={t('activity-document-photo.labels.des')}
            value={values?.des || ''}
            onChange={onChangeDescription}
            onBlur={saveDescription}
            data-testid='description-field'
            disabled={!values?.file}
            placeholder={t('activity-document-photo.placeholders.des')}
          />
          <div
            className='d-grid align-items-end'
            style={{ gridTemplateColumns: '1fr auto auto' }}
          >
            <div className='flex-grow-1'>
              <Unifree
                label={label || t('activity-document-photo.labels.uuid-foto')}
                disabled={true}
                value={values?.uuid || ''}
                required={required}
                placeholder={t(
                  'activity-document-photo.placeholders.uuid-foto'
                )}
              />
            </div>
            {values?.file ? (
              <Button
                onClick={() => setView(true)}
                variant='secondary'
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='Visualization' icSize='semiSmall' />
              </Button>
            ) : (
              <Button
                onClick={openModal}
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='TakePhoto' icSize='semiSmall' />
              </Button>
            )}
            {!values?.file && (
              <FileUploader
                onChange={uploadLocalFile}
                accept='image/*'
                variant='secondary'
                maxSizeInBytes={maxSizeInBytes}
              />
            )}
            {values?.file && (
              <Button
                variant='secondary'
                onClick={clearData}
                style={{ width: '40px', marginLeft: '4px' }}
              >
                <Icon iconName='Cleaner' icSize='semiSmall' />
              </Button>
            )}
          </div>
          <Unifree
            label={t('activity-document-photo.labels.status')}
            disabled={true}
            value={values?.uuid_status || ''}
            required={required}
          />
          <div className='row'>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-photo.labels.content-type')}
                disabled={true}
                value={values?.contentType || ''}
                required={required}
                placeholder={t(
                  'activity-document-photo.placeholders.content-type'
                )}
              />
            </div>
            <div className='col-6'>
              <Unifree
                label={t('activity-document-photo.labels.size')}
                disabled={true}
                value={values?.size ? formatFileSize(Number(values.size)) : ''}
                required={required}
                placeholder={t('activity-document-photo.placeholders.size')}
              />
            </div>
          </div>
          <div className='w-100 d-flex justify-content-center align-items-center'>
            {documentURL ? (
              <img
                src={documentURL}
                className='img-fluid'
                alt={`${values?.des || 'Photo'}`}
              />
            ) : (
              <div className='w-100 webcam-no-image rounded'>
                {t('activity-document-photo.no-image')}
              </div>
            )}
          </div>
        </div>
      )}
      {isOpenModal && (
        <WebcamCapture onSuccess={capturePhoto} onCancel={closeWebcam} />
      )}
      {view && (
        <WebcamView onCancel={() => setView(false)} image={values?.file} />
      )}
    </>
  )
}

export default ActivityDocumentPhoto
